/* eslint-disable camelcase */
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import { captureException } from '@sentry/gatsby';

import { submitFreeTrialToHubspot } from '../../Utilities/hubspotFreeTrialFormApi';
import trialSignupUser from '../../Utilities/trialSignupUser';

const errorMessages = {
  firstname: 'Please complete required field',
  lastname: 'Please complete required field',
  phone: 'Please complete required field',
  companyname: 'Please complete required field',
  Number_of_Technicians_Range__c: 'Please complete required field',
  email: 'Please complete required field',
  password: 'Please complete required field',
};

const validateRequiredField = (value, key, validation, setValidation) => {
  const stringValue = typeof value === 'string' ? value : String(value ?? '');
  if (isEmpty(stringValue)) {
    setValidation({ ...validation, [key]: errorMessages[key] });
    return false;
  }
  setValidation({ ...validation, [key]: '' });
  return true;
};

export const validateEmail = (value, validation, setValidation) => {
  if (isEmpty(value)) {
    return validateRequiredField(value, 'email', validation, setValidation);
  }

  if (!isEmail(value)) {
    setValidation({
      ...validation,
      email: 'Provide a valid email address to sign up.',
    });
    return false;
  }
  setValidation({ ...validation, email: '' });
  return true;
};

export const stripPhoneNumber = phone =>
  phone.replace(/[\s()+-]/g, '').replace(/-/g, '');

export const validatePhone = (value, validation, setValidation) => {
  const formatted = stripPhoneNumber(value);
  if (isEmpty(value)) {
    return validateRequiredField(value, 'phone', validation, setValidation);
  }
  if (formatted.length < 9 || formatted.length > 15) {
    setValidation({ ...validation, phone: 'Not a valid phone number' });
    return false;
  }
  setValidation({ ...validation, phone: '' });
  return true;
};

export const validatePassword = (value, validation, setValidation) => {
  if (isEmpty(value)) {
    return validateRequiredField(value, 'password', validation, setValidation);
  }

  const rules = [
    {
      test: /[A-Z]/,
      message: 'Password must contain at least one uppercase letter.',
    },
    {
      test: /[a-z]/,
      message: 'Password must contain at least one lowercase letter.',
    },
    { test: /[0-9]/, message: 'Password must contain at least one number.' },
  ];

  for (const rule of rules) {
    if (!rule.test.test(value)) {
      setValidation({ ...validation, password: rule.message });
      return false;
    }
  }

  if (value.length < 10) {
    setValidation({
      ...validation,
      password: 'Password must be at least 10 characters long.',
    });
    return false;
  }

  setValidation({ ...validation, password: '' });
  return true;
};

export const validateOnChange = (e, type, validation, setValidation) => {
  const { value } = e.target;
  switch (type) {
    case 'email':
      return validateEmail(value, validation, setValidation);
    case 'password':
      return validatePassword(value, validation, setValidation);
    case 'phone':
      return validatePhone(value, validation, setValidation);
    default:
      return validateRequiredField(value, type, validation, setValidation);
  }
};

export const handlePhoneOnChange = (
  e,
  validation,
  setValidation,
  data,
  setData
) => {
  e.preventDefault();
  const { value } = e.target;
  setData({ ...data, phone: value });
  validatePhone(value, validation, setValidation);
};

export const validateOnSubmit = async (data, validation, setValidation) => {
  const requiredFields = [
    'firstname',
    'lastname',
    'phone',
    'companyname',
    'Number_of_Technicians_Range__c',
  ];

  for (const key of requiredFields) {
    if (!validateRequiredField(data[key], key, validation, setValidation)) {
      return { valid: false, error: key };
    }
  }

  if (!validateEmail(data.email, validation, setValidation)) {
    return { valid: false, error: 'email' };
  }

  if (!validatePassword(data.password, validation, setValidation)) {
    return { valid: false, error: 'password' };
  }

  return { valid: true, error: '' };
};

const createFreeTrialPayload = data => {
  return {
    email: data?.email,
    firstname: data?.firstname,
    lastname: data?.lastname,
    phone: data?.phone,
    companyname: data?.companyname,
    jobtitle: data?.jobtitle,
    Number_of_Technicians_Range__c: data?.Number_of_Technicians_Range__c,
    industry: data?.industry,
    industrydetail: data?.industrydetail,
    country: data?.country,
    state: data?.state,
    url: data?.url,
    gclid: data?.gclid,
    language: data?.language,
    offer: data?.offer,
  };
};

const createUpKeepTrialSignUpPayload = (data, location) => ({
  email: data?.email?.toLowerCase().trim(),
  password: data?.password?.replace(/%20/g, '+'),
  phoneNumber: data?.phone,
  platform: 'web',
  source: location,
  firstName: data?.firstname,
  lastName: data?.lastname,
  jobTitle: data?.jobtitle || 'n/a',
  companyName: data?.companyname,
});

const handleSuccess = (
  data,
  location,
  setData,
  setNetworkError,
  setLoading
) => {
  setLoading(true);
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'free-trial-signup' });
  }

  trialSignupUser(
    createUpKeepTrialSignUpPayload(data, location.href),
    setData,
    setNetworkError,
    setLoading
  );
};

export const handleSubmit = async (
  e,
  data,
  validation,
  setValidation,
  location,
  setLoading,
  setData,
  setNetworkError,
  hubspotFormId
) => {
  e.preventDefault();

  try {
    const { valid, error } = await validateOnSubmit(
      data,
      validation,
      setValidation
    );
    if (!valid) throw new Error(`Invalid form data for field: ${error}`);

    const payload = createFreeTrialPayload(data);

    // Submit to HubSpot
    await submitFreeTrialToHubspot(payload, hubspotFormId);

    // Continue with UpKeep signup
    handleSuccess(data, location, setData, setNetworkError, setLoading);
  } catch (error) {
    captureException(`Trial Form HubSpot Error - ${error}`);
    setNetworkError(
      'There was an error submitting the form. Please try again later.'
    );
  }
};
